import { forwardRef, useEffect, useState } from 'react';
import RDP from 'react-datepicker';
import dayjs, { type Dayjs } from 'dayjs';
import { Calendar, X } from 'lucide-react';
import { cn } from './cn';

// Translate the common Ant Design (dayjs) format tokens callers pass to date-fns tokens.
function toDateFns(fmt?: string): string {
  if (!fmt) return 'dd MMM yyyy';
  return fmt.replace(/YYYY/g, 'yyyy').replace(/DD/g, 'dd').replace(/A/g, 'a');
}

const SIZE: Record<string, string> = { small: 'h-8 text-[13px]', middle: 'h-9 text-sm', large: 'h-11 text-[15px]' };

const DateInput = forwardRef<HTMLButtonElement, any>(function DateInput({ value, onClick, onClear, placeholder, size = 'middle', disabled, allowClear }, ref) {
  return (
    <button
      type="button" ref={ref} onClick={onClick} disabled={disabled}
      className={cn('inline-flex w-full min-w-[9rem] items-center gap-2 rounded-control border border-line bg-surface px-3 text-left text-content transition-colors hover:border-brand-300 focus-visible:border-brand-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/20 disabled:cursor-not-allowed disabled:opacity-60', SIZE[size])}
    >
      <Calendar className="size-4 shrink-0 text-subtle" />
      <span className={cn('flex-1 truncate', !value && 'text-subtle')}>{value || placeholder}</span>
      {allowClear && value && (
        <span role="button" tabIndex={-1} aria-label="Clear" onClick={(e) => { e.stopPropagation(); onClear?.(); }} className="grid size-5 place-items-center rounded-full text-subtle hover:bg-surface-2 hover:text-content">
          <X className="size-3.5" />
        </span>
      )}
    </button>
  );
});

/** Ant Design single `DatePicker` replacement (Dayjs value in/out), on react-datepicker. */
function DatePickerBase({
  value, onChange, format, disabledDate, allowClear = true, placeholder = 'Select date', size = 'middle',
  disabled, picker, showTime, className, style, minDate, maxDate,
}: {
  value?: Dayjs | null;
  onChange?: (d: Dayjs | null, dateString: string) => void;
  format?: string;
  disabledDate?: (d: Dayjs) => boolean;
  allowClear?: boolean;
  placeholder?: string;
  size?: 'small' | 'middle' | 'large';
  disabled?: boolean;
  picker?: 'date' | 'month' | 'year';
  showTime?: boolean | Record<string, unknown>;
  className?: string;
  style?: import('react').CSSProperties;
  minDate?: Dayjs;
  maxDate?: Dayjs;
}) {
  const df = picker === 'month' ? 'MMM yyyy' : picker === 'year' ? 'yyyy' : showTime ? `${toDateFns(format)} HH:mm` : toDateFns(format);
  return (
    <div className={cn('gb-datepicker', className)} style={style}>
      <RDP
        selected={value ? value.toDate() : null}
        onChange={(d: Date | null) => onChange?.(d ? dayjs(d) : null, d ? dayjs(d).format('YYYY-MM-DD') : '')}
        dateFormat={df}
        showMonthYearPicker={picker === 'month'}
        showYearPicker={picker === 'year'}
        showTimeSelect={!!showTime}
        filterDate={disabledDate ? (d: Date) => !disabledDate(dayjs(d)) : undefined}
        minDate={minDate ? minDate.toDate() : undefined}
        maxDate={maxDate ? maxDate.toDate() : undefined}
        disabled={disabled}
        placeholderText={placeholder}
        popperClassName="z-[var(--z-popover)]"
        customInput={<DateInput size={size} disabled={disabled} allowClear={allowClear} onClear={() => onChange?.(null, '')} />}
      />
    </div>
  );
}

/** Ant Design `DatePicker.RangePicker` replacement — two composed date fields. */
function RangePicker({
  value, onChange, disabledDate, size = 'middle', disabled, format, className, style, allowEmpty, allowClear = false, showTime, placeholder,
}: {
  value?: [Dayjs | null, Dayjs | null] | null;
  onChange?: (dates: [Dayjs | null, Dayjs | null] | null, dateStrings: [string, string]) => void;
  disabledDate?: (d: Dayjs) => boolean;
  size?: 'small' | 'middle' | 'large';
  disabled?: boolean;
  format?: string;
  className?: string;
  style?: import('react').CSSProperties;
  allowEmpty?: [boolean, boolean];
  allowClear?: boolean;
  showTime?: boolean | Record<string, unknown>;
  placeholder?: [string, string];
}) {
  // Hold the in-progress selection as an internal DRAFT. We only surface a COMPLETE range (both dates)
  // or a full clear to the parent — NEVER a half-picked [start, null]. That way every consumer can do
  // range[0].format()/range[1].format() without null-guarding; a half-pick used to blank the whole page.
  const cFrom = value?.[0] ?? null;
  const cTo = value?.[1] ?? null;
  const [draft, setDraft] = useState<[Dayjs | null, Dayjs | null]>([cFrom, cTo]);
  // Re-sync the draft when the parent changes the value (clear, preset, external set). Keyed on the
  // instants (valueOf) so a new-but-equal array reference can't wipe a half-made selection mid-pick.
  useEffect(() => { setDraft([cFrom, cTo]); }, [cFrom?.valueOf(), cTo?.valueOf()]); // eslint-disable-line react-hooks/exhaustive-deps

  const [from, to] = draft;
  const commit = (f: Dayjs | null, t: Dayjs | null) => {
    setDraft([f, t]); // always reflect the pick in the field…
    if (f && t) onChange?.([f, t], [f.format('YYYY-MM-DD'), t.format('YYYY-MM-DD')]); // …but only notify on a complete range
    else if (!f && !t) onChange?.(null, ['', '']); // …or a full clear
  };
  return (
    <div className={cn('inline-flex items-center gap-1.5', className)} style={style}>
      <DatePickerBase value={from} onChange={(d) => commit(d, to)} disabledDate={disabledDate} size={size} disabled={disabled} format={format} showTime={showTime} allowClear={allowClear} placeholder={placeholder?.[0] ?? 'Start date'} maxDate={to ?? undefined} />
      <span className="text-subtle">→</span>
      <DatePickerBase value={to} onChange={(d) => commit(from, d)} disabledDate={disabledDate} size={size} disabled={disabled} format={format} showTime={showTime} allowClear={allowClear} placeholder={placeholder?.[1] ?? 'End date'} minDate={from ?? undefined} />
    </div>
  );
}

/** Exported as `DatePicker`, with `DatePicker.RangePicker` (antd-compatible static). */
export const DatePicker = Object.assign(DatePickerBase, { RangePicker });
